<template>
  <div>
    <div class="desc" v-if=" routeName!=='register'">
      Choose a handle from your connected Instagram or TikTok to customize your storefront URL.
      <el-link :underline="false" @click="openConnct()">Connect account.</el-link>
    </div>

    <MessageBox v-if="route.name !== 'register'" :msg="store.user.can_update_user_handle ? 'You can only change your username once every 7 days.' : 'You recently updated your username. You can only change your username once every 7 days.'" />

    <div class="selcet-box for-selcet-box" v-loading="loading">
      <div class="prepend">www.shop.anchor.store/{Brand Name}/</div>
      <el-select :model-value="getHandleValue" placeholder="---Select---"
        :disabled="loading || (route.name!== 'register' && !store.user.can_update_user_handle)"
        popper-class="custom-select-picker">
        <template #label>
          <!-- always show current selection, even if icon is 'people' -->
          <span>{{ getHandleValue?.handle ? (getHandleValue.handle.startsWith('@') ? getHandleValue.handle : '@' + getHandleValue.handle) : '---Select---' }}</span>
        </template>

        <!-- only show items with a recognized social icon (no 'people' placeholder) -->
        <el-option v-for="(item, index) in selectableList" :key="index" class="items-center" :value="`${item.handle}-fake`"
          :class="{ 'is-selected': getHandleValue?.handle === item.handle }"
          @click="handleSelect(item)">
          <svg-icon :name="item.icon" />
          <span>{{ item.handle }}</span>
        </el-option>
      </el-select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useRegisterStore } from '@/stores/RegisterStore';
import MessageBox from '@shared/components/ui/MessageBox.vue';
import emitter from '@shared/utils/EventBus';
import { useRoute } from 'vue-router';

const store = useGlobalStore();
const registerStore = useRegisterStore();
const emit = defineEmits(['updateHandle']);
const props = defineProps({
  handleValue: {
    type: String,
    default: ''
  },
});

const getHandleValue = ref(null);
const socialHandleList = ref([]);
const loading = ref(false);
const route = useRoute();

const routeName= computed(() => route.name);

// In non-register mode: only expose items with a known social icon as selectable options.
// The label (<template #label>) still shows whatever is selected (including 'people' items).
const selectableList = computed(() => {
  return socialHandleList.value.filter(item => item.icon !== 'people');
});

const openConnct = () => {
  emitter.emit('open-connect-dialog');
};

const handleSelect = (item) => {
  getHandleValue.value = item;
  emit('updateHandle', item.handle);
};

const buildHandleList = (sourceList) => {
  socialHandleList.value = [];
  const isRegister = window.location.href.includes('register');

  sourceList.forEach(e => {
    const lookupKey = e.from;
    const social = store.initSocial.find(i => i.backName.includes(lookupKey));
    socialHandleList.value.push({ ...e, icon: social?.icon ?? 'people' });
  });

  socialHandleList.value.forEach(e => {
    if (e.handle === props.handleValue) {
      getHandleValue.value = e;
    }
  });

  if (getHandleValue.value === null) {
    if (isRegister) {
      // auto-select first non-people item without emitting; NEXT will confirm via confirmHandle()
      const firstSelectable = socialHandleList.value.find(item => item.icon !== 'people');
      getHandleValue.value = firstSelectable ?? socialHandleList.value[0] ?? null;
    } else {
      handleSelect(socialHandleList.value[0]);
    }
  }
};

const confirmHandle = () => {
  if (getHandleValue.value) {
    handleSelect(getHandleValue.value);
  }
};

defineExpose({ confirmHandle });

onMounted(async () => {
  if (window.location.href.includes('register')) {
    buildHandleList(registerStore.spHandle ?? []);
  } else {
    buildHandleList(store.user?.user_handle_list ?? []);
  }
});
</script>

<style lang="scss">
.for-selcet-box {
  display: grid;
  grid-template-columns: auto 1fr;
  width: 100%;
  margin-top: 8px;

  @media (max-width: $mobile_size) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .prepend {
    color: var(--Text-primary);
    background-color: var(--Neu-30);
    padding: 0 16px;
    @include flexCenter;
    border-radius: 6px 0 0 6px;

    @media (max-width: $mobile_size) {
      border-radius: var(--radius);
      height: 40px;
    }
  }

  .ui-select {
    margin: 0;
  }

  .ui-select .ui-select__wrapper {
    width: 100%;
    border-radius: 0px 6px 6px 0px;
    max-height: 40px;
    height: 40px !important;

    @media (max-width: $mobile_size) {
      border-radius: var(--radius);
    }
    // .svg-icon {
    //   color: var(--Black-100);
    // }

    &.is-disabled {
      background-color: var(--Black-00);

      .ui-select__selected-item {
        color: var(--Text-secondary);
      }
    }
  }
}
</style>
